import {
  DecoratorNode,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
  type LexicalUpdateJSON,
} from "lexical";
import { createElement } from "react";

import { OfferChip } from "./OfferChip";

/**
 * What the node stores and serializes: its slug, the text shown in the chip,
 * and the fallback code used when the message is rendered. Generic on purpose —
 * the node knows nothing about offer-preview semantics; hosts map their own
 * offer shape onto `label`. Reused as the insert-command payload so the node
 * and command can't drift.
 */
export interface OfferNodeData {
  slug: string;
  /** Text rendered in the chip (e.g. a code preview or `##########`). */
  label: string;
  fallbackCode: string;
}

/**
 * A selectable offer as a host (e.g. InlineLexicalInput) describes it. `name`
 * and `preview` are kept separate to mirror how the rest of the app renders
 * offers: the picker lists the human `name`, while the chip shows `preview` —
 * the code preview from `getOfferPreview` (a static code, a connector pattern,
 * or `##########` for a dynamic offer whose code is generated per recipient).
 */
export interface OfferDefinition {
  slug: string;
  /** Human-readable name shown in the picker, e.g. "Summer Sale". */
  name: string;
  /** Code preview shown in the chip; `##########` for dynamic offers. */
  preview: string;
  fallbackCode: string;
  /**
   * Whether the offer renders the same code for every recipient. Drives the
   * S/U badge in the picker; matches the static-vs-unique distinction used
   * by the other offer dropdowns in the app.
   */
  isStatic: boolean;
}

export type SerializedOfferNode = Spread<OfferNodeData, SerializedLexicalNode>;

export class OfferNode extends DecoratorNode<ReturnType<typeof createElement>> {
  __slug: string;
  __label: string;
  __fallbackCode: string;

  static getType(): string {
    return "offer-code";
  }

  static clone(node: OfferNode): OfferNode {
    return new OfferNode(
      node.__slug,
      node.__label,
      node.__fallbackCode,
      node.__key,
    );
  }

  static importJSON(serializedNode: SerializedOfferNode): OfferNode {
    return new OfferNode(
      serializedNode.slug,
      serializedNode.label,
      serializedNode.fallbackCode,
    ).updateFromJSON(serializedNode);
  }

  constructor(
    slug: string,
    label: string,
    fallbackCode: string,
    key?: NodeKey,
  ) {
    super(key);
    this.__slug = slug;
    this.__label = label;
    this.__fallbackCode = fallbackCode;
  }

  updateFromJSON(serializedNode: LexicalUpdateJSON<SerializedOfferNode>): this {
    return super.updateFromJSON(serializedNode);
  }

  exportJSON(): SerializedOfferNode {
    return {
      ...super.exportJSON(),
      slug: this.__slug,
      label: this.__label,
      fallbackCode: this.__fallbackCode,
    };
  }

  createDOM(): HTMLElement {
    const span = document.createElement("span");
    span.style.display = "contents";
    return span;
  }

  updateDOM(): false {
    return false;
  }

  isInline(): boolean {
    return true;
  }

  isIsolated(): boolean {
    return true;
  }

  isKeyboardSelectable(): boolean {
    return true;
  }

  setOffer(slug: string, label: string, fallbackCode: string): this {
    const self = this.getWritable();
    self.__slug = slug;
    self.__label = label;
    self.__fallbackCode = fallbackCode;
    return self;
  }

  getTextContent(): string {
    return `{% offer '${this.__slug}' %}`;
  }

  decorate(): ReturnType<typeof createElement> {
    return createElement(OfferChip, {
      nodeKey: this.__key,
      slug: this.__slug,
      label: this.__label,
    });
  }
}

export function $createOfferNode(
  slug: string,
  label: string,
  fallbackCode: string,
): OfferNode {
  return new OfferNode(slug, label, fallbackCode);
}

export function $isOfferNode(
  node: LexicalNode | null | undefined,
): node is OfferNode {
  return node instanceof OfferNode;
}
